import * as THREE from "../../../libs/three.js/build/three.module.js";
import {CameraMode} from "../../defines.js";

import {t} from "../../digitization/i18n/index.js";
// Gemt visning ("viewpoint", som i BCF) og skærmbillede til POI'er. Rent værktøj til værten: ingen POI-viden, ingen lagring.
//
//   Potree.captureViewpoint(viewer)                   -> {v, position:[x,y,z], target:[x,y,z], mode, fov}
//   Potree.applyViewpoint(viewer, viewpoint, ms)      flyver kameraet derhen (gendanner også perspektiv/ortografisk)
//   Potree.flyToPoint(viewer, point, distance, ms)    kigger på et punkt fra kameraets nuværende retning (POI uden gemt visning)
//   Potree.captureViewerImage(viewer, {maxWidth})     -> Promise<{blob, width, height}> (JPEG af det aktuelle billede)

const asArray = (v) => (Array.isArray(v) ? v : [v.x, v.y, v.z]);

export function captureViewpoint(viewer){
	const view = viewer.scene.view;
	const position = view.position;
	const target = view.getPivot();
	return {
		v: 1,
		position: [position.x, position.y, position.z],
		target: [target.x, target.y, target.z],
		mode: viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC ? "orthographic" : "perspective",
		fov: typeof viewer.getFOV === "function" ? viewer.getFOV() : null,
	};
}

function requestRedraw(viewer){
	if(typeof viewer._requestRedraw === "function") viewer._requestRedraw(); else viewer.needsRedraw = true;
}

export function applyViewpoint(viewer, viewpoint, duration = 700){
	if(!viewpoint || !Array.isArray(viewpoint.position) || !Array.isArray(viewpoint.target)) return false;

	const wantOrtho = viewpoint.mode === "orthographic";
	const isOrtho = viewer.scene.cameraMode === CameraMode.ORTHOGRAPHIC;
	if(wantOrtho !== isOrtho){
		viewer.setCameraMode(wantOrtho ? CameraMode.ORTHOGRAPHIC : CameraMode.PERSPECTIVE);
	}

	viewer.scene.view.setView(viewpoint.position, viewpoint.target, duration);
	requestRedraw(viewer);
	return true;
}

// Et POI uden gemt visning (fx åbnet fra et link med kun et punkt): kameraet bevarer sin retning og flyttes til `distance` meter fra
// punktet, så det er tydeligt at se - eller, hvis kameraet allerede er tættere end det, blot drejet mod punktet.
export function flyToPoint(viewer, point, distance = 12, duration = 700){
	const target = new THREE.Vector3(...asArray(point));
	const view = viewer.scene.view;
	const direction = new THREE.Vector3().subVectors(view.position, target);
	if(direction.lengthSq() < 1e-6) direction.set(0, -1, 0.7);
	direction.normalize();
	// ikke vandret ind i terrænet: mindst en let nedadgående blikretning
	if(direction.z < 0.15) direction.z = 0.15;
	direction.normalize();
	const position = target.clone().addScaledVector(direction, distance);
	view.setView(position, target, duration);
	requestRedraw(viewer);
	return true;
}

// Skærmbillede af det, vieweren tegner lige nu (punktsky og 3D-objekter; DOM-etiketter og nåle er ikke med). viewer.render() tegner
// synkront, og billedet kopieres, inden opgaven slipper - så WebGL-bufferen stadig er gyldig, også uden preserveDrawingBuffer.
export function captureViewerImage(viewer, {maxWidth = 1280, type = "image/jpeg", quality = 0.85} = {}){
	viewer.render();
	const source = viewer.renderer.domElement;
	const scale = Math.min(1, maxWidth / Math.max(1, source.width));
	const width = Math.max(1, Math.round(source.width * scale));
	const height = Math.max(1, Math.round(source.height * scale));

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext("2d");
	// JPEG har ingen alfa: hvid bund, så gennemsigtige områder ikke bliver sorte
	context.fillStyle = "#ffffff";
	context.fillRect(0, 0, width, height);
	context.drawImage(source, 0, 0, width, height);

	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if(blob) resolve({blob, width, height}); else reject(new Error(t("Skærmbilledet kunne ikke oprettes")));
		}, type, quality);
	});
}
